import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { usePageT } from "./i18n/LanguageContext";
import LanguageSwitcher from "./components/LanguageSwitcher";

const LOGO_URL = "https://nautiqo.cloud/logo-nautiqo.png";
const API = `${process.env.REACT_APP_API_URL || "https://api.nautiqo.cloud"}/api`;
const APP_STORE_URL = "https://apps.apple.com/app/id6762418993";

const heading = { fontFamily: "'Outfit', sans-serif" };

const translations = {
  it: {
    missingToken: "Token di invito mancante.",
    invalidInvite: "Invito non valido o scaduto.",
    passwordTooShort: "La password deve avere almeno 6 caratteri.",
    passwordMismatch: "Le password non coincidono.",
    acceptError: "Errore durante l'accettazione dell'invito.",
    verifying: "Verifica invito...",
    invalidTitle: "Invito non valido",
    contactSupport: "Contatta il supporto →",
    acceptedTitle: "Invito accettato!",
    acceptedText1: "Account creato con successo.",
    acceptedText2: "Scarica l'app per accedere.",
    downloadAppStore: "Scarica su App Store",
    invitedTitle: "Sei stato invitato",
    invitedTeam: "a far parte del team di",
    labelName: "Nome",
    labelEmail: "Email",
    labelRole: "Ruolo",
    roleStaff: "Staff",
    choosePassword: "Scegli una password",
    passwordPlaceholder: "Minimo 6 caratteri",
    confirmPassword: "Conferma password",
    confirmPasswordPlaceholder: "Ripeti la password",
    creatingAccount: "Creazione account...",
    acceptAndCreate: "Accetta invito e crea account",
    expiryNote: "Questo invito scade dopo 7 giorni. Se non te lo aspettavi, ignoralo.",
    rightsReserved: "Tutti i diritti riservati.",
  },
  en: {
    missingToken: "Invitation token missing.",
    invalidInvite: "Invitation invalid or expired.",
    passwordTooShort: "The password must be at least 6 characters.",
    passwordMismatch: "The passwords do not match.",
    acceptError: "An error occurred while accepting the invitation.",
    verifying: "Verifying invitation...",
    invalidTitle: "Invalid invitation",
    contactSupport: "Contact support →",
    acceptedTitle: "Invitation accepted!",
    acceptedText1: "Account created successfully.",
    acceptedText2: "Download the app to sign in.",
    downloadAppStore: "Download on the App Store",
    invitedTitle: "You've been invited",
    invitedTeam: "to join the team at",
    labelName: "Name",
    labelEmail: "Email",
    labelRole: "Role",
    roleStaff: "Staff",
    choosePassword: "Choose a password",
    passwordPlaceholder: "Minimum 6 characters",
    confirmPassword: "Confirm password",
    confirmPasswordPlaceholder: "Repeat the password",
    creatingAccount: "Creating account...",
    acceptAndCreate: "Accept invitation and create account",
    expiryNote: "This invitation expires after 7 days. If you weren't expecting it, please ignore it.",
    rightsReserved: "All rights reserved.",
  },
  es: {
    missingToken: "Falta el token de invitación.",
    invalidInvite: "Invitación no válida o caducada.",
    passwordTooShort: "La contraseña debe tener al menos 6 caracteres.",
    passwordMismatch: "Las contraseñas no coinciden.",
    acceptError: "Se produjo un error al aceptar la invitación.",
    verifying: "Verificando invitación...",
    invalidTitle: "Invitación no válida",
    contactSupport: "Contactar con soporte →",
    acceptedTitle: "¡Invitación aceptada!",
    acceptedText1: "Cuenta creada correctamente.",
    acceptedText2: "Descarga la app para acceder.",
    downloadAppStore: "Descargar en App Store",
    invitedTitle: "Has sido invitado",
    invitedTeam: "a formar parte del equipo de",
    labelName: "Nombre",
    labelEmail: "Email",
    labelRole: "Rol",
    roleStaff: "Personal",
    choosePassword: "Elige una contraseña",
    passwordPlaceholder: "Mínimo 6 caracteres",
    confirmPassword: "Confirmar contraseña",
    confirmPasswordPlaceholder: "Repite la contraseña",
    creatingAccount: "Creando cuenta...",
    acceptAndCreate: "Aceptar invitación y crear cuenta",
    expiryNote: "Esta invitación caduca después de 7 días. Si no la esperabas, ignórala.",
    rightsReserved: "Todos los derechos reservados.",
  },
  fr: {
    missingToken: "Jeton d'invitation manquant.",
    invalidInvite: "Invitation non valide ou expirée.",
    passwordTooShort: "Le mot de passe doit comporter au moins 6 caractères.",
    passwordMismatch: "Les mots de passe ne correspondent pas.",
    acceptError: "Une erreur s'est produite lors de l'acceptation de l'invitation.",
    verifying: "Vérification de l'invitation...",
    invalidTitle: "Invitation non valide",
    contactSupport: "Contacter le support →",
    acceptedTitle: "Invitation acceptée !",
    acceptedText1: "Compte créé avec succès.",
    acceptedText2: "Téléchargez l'application pour vous connecter.",
    downloadAppStore: "Télécharger sur l'App Store",
    invitedTitle: "Vous avez été invité",
    invitedTeam: "à rejoindre l'équipe de",
    labelName: "Nom",
    labelEmail: "E-mail",
    labelRole: "Rôle",
    roleStaff: "Personnel",
    choosePassword: "Choisissez un mot de passe",
    passwordPlaceholder: "Minimum 6 caractères",
    confirmPassword: "Confirmer le mot de passe",
    confirmPasswordPlaceholder: "Répétez le mot de passe",
    creatingAccount: "Création du compte...",
    acceptAndCreate: "Accepter l'invitation et créer le compte",
    expiryNote: "Cette invitation expire au bout de 7 jours. Si vous ne l'attendiez pas, ignorez-la.",
    rightsReserved: "Tous droits réservés.",
  },
  de: {
    missingToken: "Einladungstoken fehlt.",
    invalidInvite: "Einladung ungültig oder abgelaufen.",
    passwordTooShort: "Das Passwort muss mindestens 6 Zeichen lang sein.",
    passwordMismatch: "Die Passwörter stimmen nicht überein.",
    acceptError: "Beim Annehmen der Einladung ist ein Fehler aufgetreten.",
    verifying: "Einladung wird überprüft...",
    invalidTitle: "Ungültige Einladung",
    contactSupport: "Support kontaktieren →",
    acceptedTitle: "Einladung angenommen!",
    acceptedText1: "Konto erfolgreich erstellt.",
    acceptedText2: "Lade die App herunter, um dich anzumelden.",
    downloadAppStore: "Im App Store laden",
    invitedTitle: "Du wurdest eingeladen",
    invitedTeam: "dem Team beizutreten von",
    labelName: "Name",
    labelEmail: "E-Mail",
    labelRole: "Rolle",
    roleStaff: "Mitarbeiter",
    choosePassword: "Wähle ein Passwort",
    passwordPlaceholder: "Mindestens 6 Zeichen",
    confirmPassword: "Passwort bestätigen",
    confirmPasswordPlaceholder: "Passwort wiederholen",
    creatingAccount: "Konto wird erstellt...",
    acceptAndCreate: "Einladung annehmen und Konto erstellen",
    expiryNote: "Diese Einladung läuft nach 7 Tagen ab. Falls du sie nicht erwartet hast, ignoriere sie.",
    rightsReserved: "Alle Rechte vorbehalten.",
  },
};

const AcceptInvitePage = () => {
  const t = usePageT(translations);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t.missingToken);
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/staff/invite-info?token=${encodeURIComponent(token)}`)
      .then((res) => setInvite(res.data))
      .catch((err) =>
        setError(err.response?.data?.detail || t.invalidInvite)
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (password.length < 6) {
      setFormError(t.passwordTooShort);
      return;
    }
    if (password !== confirmPassword) {
      setFormError(t.passwordMismatch);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/staff/accept-invite`, { token, password });
      setSuccess(true);
    } catch (err) {
      setFormError(
        err.response?.data?.detail || t.acceptError
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Nav */}
      <nav className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/landing" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-[22%] overflow-hidden flex-shrink-0">
              <img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" />
            </div>
            <span className="font-bold text-xl text-slate-900 tracking-tight" style={heading}>
              Nautiqo
            </span>
          </Link>
          <LanguageSwitcher variant="light" />
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-4 py-12">
        {/* Loading */}
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-2 border-slate-200 border-t-slate-700 rounded-full animate-spin mb-4" />
            <p className="text-slate-500 text-sm">{t.verifying}</p>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={heading}>{t.invalidTitle}</h1>
            <p className="text-slate-500 mb-8">{error}</p>
            <a href="mailto:info@nautiqo.cloud" className="text-sm text-slate-400 hover:text-slate-700 transition-colors">
              {t.contactSupport}
            </a>
          </div>
        )}

        {/* Success state */}
        {!loading && success && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={heading}>{t.acceptedTitle}</h1>
            <p className="text-slate-500 mb-8">
              {t.acceptedText1}<br />{t.acceptedText2}
            </p>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-slate-900 text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-slate-700 transition-colors"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              {t.downloadAppStore}
            </a>
          </div>
        )}

        {/* Form */}
        {!loading && invite && !success && (
          <>
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1" style={heading}>{t.invitedTitle}</h1>
              <p className="text-slate-500 text-sm">
                {t.invitedTeam}{" "}
                <strong className="text-slate-700">{invite.company_name}</strong>
              </p>
            </div>

            {/* Invite details */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-200 mb-6">
              <div className="flex justify-between px-4 py-3">
                <span className="text-sm text-slate-500">{t.labelName}</span>
                <span className="text-sm font-medium text-slate-900">
                  {invite.first_name} {invite.last_name}
                </span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-sm text-slate-500">{t.labelEmail}</span>
                <span className="text-sm font-medium text-slate-900">{invite.email}</span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-sm text-slate-500">{t.labelRole}</span>
                <span className="text-sm font-medium text-slate-900 capitalize">
                  {invite.role === "staff" ? t.roleStaff : invite.role}
                </span>
              </div>
            </div>

            {/* Password form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  {t.choosePassword}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFormError(""); }}
                  placeholder={t.passwordPlaceholder}
                  required
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  {t.confirmPassword}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setFormError(""); }}
                  placeholder={t.confirmPasswordPlaceholder}
                  required
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition"
                />
              </div>

              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-slate-900 text-white font-semibold py-3.5 rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-60 mt-2"
              >
                {submitting ? t.creatingAccount : t.acceptAndCreate}
              </button>
            </form>

            <p className="text-center text-xs text-slate-400 mt-6">
              {t.expiryNote}
            </p>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 mt-12">
        <div className="max-w-lg mx-auto px-4 text-center text-sm text-slate-400">
          <p>&copy; {new Date().getFullYear()} Nautiqo. {t.rightsReserved}</p>
        </div>
      </footer>
    </div>
  );
};

export default AcceptInvitePage;
